import "./SettingsModal.css";
import { useEffect, useRef, useState } from "react";
import {
  profileIcon,
  subscriptionsIcon,
  billingIcon,
  usageHistoryIcon,
  settingsNavIcon,
} from "../../icons/assets";
import ProfileSettings from "./account/profile/ProfileSettings";
import AppSettings from "./account/settings/AppSettings";
import SubscriptionSettings from "./account/subscriptions/SubscriptionSettings";
import BillingHistory from "./account/billing/BillingHistory";
import UsageHistory from "./account/usage/UsageHistory";

const MENU_ITEMS = [
  { id: "profile", label: "Profile", icon: profileIcon },
  { id: "subscriptions", label: "Subscriptions", icon: subscriptionsIcon },
  { id: "billingHistory", label: "Billing History", icon: billingIcon },
  { id: "usageHistory", label: "Usage History", icon: usageHistoryIcon },
  { id: "settings", label: "Settings", icon: settingsNavIcon },
];

const SettingsModal = ({
  onClose,
  initialActiveItem = "profile",
  lifetimeCredits = 0,
  monthlyCredits = 0,
  storageQuotaGb = 2,
  storageUsedGb = 0,
  onChangePlan,
  onCreditsRefresh,
  suppressClose = false,
}) => {
  const modalRef = useRef(null);
  const [activeItem, setActiveItem] = useState(initialActiveItem);

  useEffect(() => {
    const next = initialActiveItem || "profile";
    setActiveItem(next === "credentials" ? "settings" : next);
  }, [initialActiveItem]);

  useEffect(() => {
    if (suppressClose) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, suppressClose]);

  const handleOverlayClick = (e) => {
    if (suppressClose) return;
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const creditCap = Math.max(
    1000,
    Number(lifetimeCredits) || 0,
    Number(monthlyCredits) || 0
  );
  const creditsProgress = Math.max(
    0,
    Math.min(((Number(lifetimeCredits) || 0) / creditCap) * 100, 100)
  );
  const storageProgress = Math.max(
    0,
    Math.min(
      storageQuotaGb > 0 ? (storageUsedGb / storageQuotaGb) * 100 : 0,
      100
    )
  );

  return (
    <div
      className="account-settings-modal-overlay"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className="account-settings-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-settings-modal-title"
      >
        <button
          type="button"
          className="account-settings-modal__close"
          onClick={onClose}
          aria-label="Close account settings"
        >
          ×
        </button>

        <header className="account-settings-modal__header">
          <h2 id="account-settings-modal-title">Account</h2>
        </header>

        <div className="account-settings-modal__divider" />

        <div className="account-settings-modal__body">
          <aside className="account-settings-modal__rail">
            <nav className="account-settings-modal__nav" aria-label="Account sections">
              {MENU_ITEMS.map((item) => {
                const isActive = activeItem === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={
                      isActive
                        ? "account-settings-modal__nav-item account-settings-modal__nav-item--active"
                        : "account-settings-modal__nav-item"
                    }
                    onClick={() => setActiveItem(item.id)}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <img src={item.icon} alt="" aria-hidden="true" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div
              className="account-settings-modal__nav-usage"
              aria-label="Credits and storage"
            >
              <div className="account-nav-credit-pills">
                <div className="account-nav-credit-pill">
                  <span className="account-nav-credit-pill__count">
                    {Number(lifetimeCredits || 0).toLocaleString()}
                  </span>
                  <span className="account-nav-credit-pill__period">Lifetime</span>
                  <span className="account-nav-credit-pill__label">Credits</span>
                </div>
                <div className="account-nav-credit-pill">
                  <span className="account-nav-credit-pill__count">
                    {Number(monthlyCredits || 0).toLocaleString()}
                  </span>
                  <span className="account-nav-credit-pill__period">Monthly</span>
                  <span className="account-nav-credit-pill__label">Credits</span>
                </div>
              </div>

              <div className="account-nav-meter">
                <div
                  className="account-nav-meter__track"
                  role="progressbar"
                  aria-label="Credits balance"
                  aria-valuemin={0}
                  aria-valuemax={creditCap}
                  aria-valuenow={Number(lifetimeCredits) || 0}
                >
                  <div
                    className="account-nav-meter__fill account-nav-meter__fill--credits"
                    style={{ width: `${creditsProgress}%` }}
                  />
                </div>
                <div className="account-nav-meter__labels">
                  <span>Credits</span>
                  <span>
                    {Number(lifetimeCredits || 0).toLocaleString()} /{" "}
                    {creditCap.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="account-nav-meter">
                <div
                  className="account-nav-meter__track"
                  role="progressbar"
                  aria-label="Storage used"
                  aria-valuemin={0}
                  aria-valuemax={storageQuotaGb}
                  aria-valuenow={storageUsedGb}
                >
                  <div
                    className="account-nav-meter__fill account-nav-meter__fill--storage"
                    style={{ width: `${storageProgress}%` }}
                  />
                </div>
                <div className="account-nav-meter__labels">
                  <span>Storage used</span>
                  <span>
                    {storageUsedGb} GB / {storageQuotaGb} GB
                  </span>
                </div>
              </div>
            </div>
          </aside>

          <div className="account-settings-modal__panel">
            {activeItem === "profile" && (
              <ProfileSettings onChangePlan={onChangePlan} />
            )}
            {activeItem === "settings" && <AppSettings />}
            {activeItem === "subscriptions" && (
              <SubscriptionSettings
                onChangePlan={onChangePlan}
                onSubscriptionChanged={onCreditsRefresh}
              />
            )}
            {activeItem === "billingHistory" && <BillingHistory />}
            {activeItem === "usageHistory" && <UsageHistory />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
